/**
 * Workspace store — Personal vs organization context for API calls and UI.
 */

import { create } from 'zustand';
import {
  listOrganizations,
  OrganizationListItem,
  invalidateBillingInfoCache,
} from '../lib/api';
import {
  getRequestOrganizationId,
  setRequestOrganizationId,
} from '../lib/workspaceRequestContext';
import { useBillingStore } from './billingStore';

const PERSONAL_STORAGE_VALUE = 'personal';

function workspaceStorageKey(userId: string): string {
  return `layerre_workspace_${userId}`;
}

function readPersistedOrgId(userId: string): string | null {
  try {
    const raw = localStorage.getItem(workspaceStorageKey(userId));
    if (!raw || raw === PERSONAL_STORAGE_VALUE) return null;
    return raw;
  } catch {
    return null;
  }
}

function persistOrgId(userId: string | null, orgId: string | null): void {
  if (!userId) return;
  try {
    if (orgId) {
      localStorage.setItem(workspaceStorageKey(userId), orgId);
    } else {
      localStorage.setItem(workspaceStorageKey(userId), PERSONAL_STORAGE_VALUE);
    }
  } catch {
    // ignore
  }
}

interface WorkspaceStore {
  currentOrganizationId: string | null;
  organizations: OrganizationListItem[];
  personalPlanTier: string;
  isLoadingOrgs: boolean;
  workspaceHydrated: boolean;
  loadOrgs: (userId: string, options?: { force?: boolean }) => Promise<void>;
  setWorkspace: (organizationId: string | null, userId?: string | null) => void;
  reset: () => void;
}

export const useWorkspaceStore = create<WorkspaceStore>((set, get) => ({
  currentOrganizationId: getRequestOrganizationId(),
  organizations: [],
  personalPlanTier: 'free',
  isLoadingOrgs: false,
  workspaceHydrated: false,

  loadOrgs: async (userId: string, options?: { force?: boolean }) => {
    const { isLoadingOrgs, workspaceHydrated } = get();
    if (isLoadingOrgs) return;
    if (workspaceHydrated && !options?.force) {
      return;
    }

    set({ isLoadingOrgs: true });
    try {
      const { organizations, personal_plan_tier } = await listOrganizations();
      let nextId = get().currentOrganizationId;

      if (!workspaceHydrated) {
        const persisted = readPersistedOrgId(userId);
        if (persisted && organizations.some(o => o.id === persisted)) {
          nextId = persisted;
        }
      }

      if (nextId && !organizations.some(o => o.id === nextId)) {
        nextId = null;
      }

      set({
        organizations,
        personalPlanTier: personal_plan_tier || 'free',
        isLoadingOrgs: false,
        workspaceHydrated: true,
      });

      if (nextId !== get().currentOrganizationId) {
        get().setWorkspace(nextId, userId);
      } else {
        setRequestOrganizationId(nextId);
        set({ currentOrganizationId: nextId });
      }
    } catch {
      set({ isLoadingOrgs: false });
      throw new Error('Failed to load organizations');
    }
  },

  setWorkspace: (organizationId: string | null, userId?: string | null) => {
    setRequestOrganizationId(organizationId);
    set({ currentOrganizationId: organizationId });
    if (userId) {
      persistOrgId(userId, organizationId);
    }
    invalidateBillingInfoCache();
    useBillingStore.getState().clearBillingInfo();
  },

  reset: () => {
    setRequestOrganizationId(null);
    set({
      currentOrganizationId: null,
      organizations: [],
      personalPlanTier: 'free',
      isLoadingOrgs: false,
      workspaceHydrated: false,
    });
    invalidateBillingInfoCache();
    useBillingStore.getState().clearBillingInfo();
  },
}));

export function getCurrentWorkspaceLabel(): string {
  const { currentOrganizationId, organizations } = useWorkspaceStore.getState();
  if (!currentOrganizationId) return 'Personal';
  return (
    organizations.find(o => o.id === currentOrganizationId)?.name ??
    'Organization'
  );
}
